// The read-only "who can do what" reference, as an INFO PANEL.
//
// It lives on /organizations, under the Members card — next to where roles are
// actually assigned, which is the only place the question "what does org_admin
// mean?" gets asked. It deliberately does NOT own a nav slot: the matrix is
// reference material, not a destination.
//
// COLLAPSED by default and fetched lazily, so an org's page costs nothing until
// someone opens it.
//
// The table is DATA, declared in shared/access-policy.js beside the rule it
// documents and served by GET /api/authz/capabilities — so changing what a role
// may do and changing this panel are the same edit. Read-only by construction:
// the only control here is the disclosure toggle.
//
// SCOPED BY THE SERVER: the response carries only the roles the caller may
// assign, so an org admin never receives the platform_admin column or the
// capabilities only platform_admin holds. This component renders whatever
// `roles` it is given and must never assume the full four.
import { useCallback, useState } from "react";
import { Check, ChevronDown, ChevronRight, KeyRound, Loader2, Minus } from "lucide-react";
import { capabilityMatrix } from "@/services/permissions";

const ROLE_LABEL = {
  platform_admin: "Platform admin",
  org_admin: "Org admin",
  member: "Member",
  app_user: "App user",
};

const ROLE_BLURB = {
  platform_admin: "Runs the platform. Cross-org: one membership row anywhere grants it everywhere.",
  org_admin: "Runs one organization — its members, its projects. No reach outside it.",
  member: "Works inside projects they own, that are org-visible, or that are shared with them.",
  app_user: "Sees only the apps explicitly shared with them. Nothing else in the product.",
};

// Cell grammar mirrors shared/access-policy.js: yes | own | cond | no.
function Cell({ value }) {
  if (value === "yes") {
    return <span className="inline-flex text-emerald-600" title="Always"><Check size={15} strokeWidth={2.5} /></span>;
  }
  if (value === "own") {
    return (
      <span className="inline-flex items-center gap-1 text-blue-600 text-xs whitespace-nowrap" title="Only within their own scope">
        <Check size={13} strokeWidth={2.5} /> own
      </span>
    );
  }
  if (value === "cond") {
    return <span className="text-xs text-amber-600 whitespace-nowrap" title="Conditional — see the note">limited</span>;
  }
  return <span className="text-page-muted/50" title="Never"><Minus size={14} /></span>;
}

export default function PermissionsMatrix() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  // Lazy: the first expand fetches; collapsing keeps what we already have.
  const toggle = useCallback(() => {
    setOpen((wasOpen) => {
      if (!wasOpen && !data && !error) {
        capabilityMatrix()
          .then((d) => { setData(d); setError(null); })
          .catch((e) => setError(e?.message || "failed to load the permissions reference"));
      }
      return !wasOpen;
    });
  }, [data, error]);

  const roles = data?.roles || [];
  // A narrowed view (org admin): the payload itself omits everything above the
  // caller, so wording that describes the whole platform would be wrong here.
  // Derived from the payload itself rather than a separate flag, so the wording
  // can never disagree with the columns actually rendered.
  const scoped = !!data && !roles.includes("platform_admin");
  const Chevron = open ? ChevronDown : ChevronRight;

  return (
    <div className="bg-card-bg rounded-[8px] overflow-hidden" data-testid="permissions-matrix">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        data-testid="permissions-matrix-toggle"
        className="w-full flex items-center gap-2 px-5 py-3 bg-transparent border-0 cursor-pointer text-left hover:bg-page-bg transition-colors"
      >
        <Chevron size={16} className="text-page-muted shrink-0" />
        <h2 className="text-base font-semibold flex items-center gap-2">
          <KeyRound size={16} /> What each role may do
        </h2>
        <span className="text-xs text-page-muted ml-2">read-only reference</span>
      </button>

      {open && (
        <div className="border-t border-page-divider px-5 py-4 flex flex-col gap-4" data-testid="permissions-matrix-body">
          <p className="text-xs text-page-muted">
            {scoped
              ? "The roles you can assign, and what each one may do. The same in every organization you administer — this is the authorization model, not this org's settings. Roles are assigned above."
              : "The same for every organization — this is the platform's authorization model, not this org's settings. Roles are assigned above."}
          </p>

          {error && <div className="bg-red-50 text-red-800 px-4 py-2 rounded-[5px] text-sm" data-testid="perm-error">{error}</div>}
          {!data && !error && (
            <div className="text-page-muted text-sm"><Loader2 className="inline animate-spin mr-2" size={16} />Loading…</div>
          )}

          {data && (
            <>
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                {roles.map((r) => (
                  <div key={r} className="border border-page-divider rounded-[5px] px-3 py-2.5" data-testid={`perm-role-${r}`}>
                    <div className="text-sm font-medium text-page-title">{ROLE_LABEL[r] || r}</div>
                    <div className="text-xs text-page-muted font-mono mt-0.5">{r}</div>
                    <p className="text-xs text-page-muted mt-1.5 leading-relaxed">{ROLE_BLURB[r]}</p>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap items-center gap-4 text-xs text-page-muted">
                <span className="inline-flex items-center gap-1.5"><Check size={13} className="text-emerald-600" strokeWidth={2.5} /> always</span>
                <span className="inline-flex items-center gap-1.5"><Check size={13} className="text-blue-600" strokeWidth={2.5} /> own — only within their own org or project</span>
                <span className="inline-flex items-center gap-1.5"><span className="text-amber-600">limited</span> — conditional, see the note</span>
                <span className="inline-flex items-center gap-1.5"><Minus size={13} className="text-page-muted/50" /> never</span>
              </div>

              {data.groups.map((group) => (
                <div key={group.group} className="border border-page-divider rounded-[5px] overflow-hidden" data-testid={`perm-group-${group.group}`}>
                  <div className="px-3 py-2 text-sm font-medium text-page-title bg-page-bg">{group.group}</div>
                  {/* Wide table on a narrow viewport: scroll the table, not the page. */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse min-w-[620px]">
                      <thead>
                        <tr className="text-xs text-page-muted">
                          <th className="text-left font-normal px-3 py-2 w-1/2">Permission</th>
                          {roles.map((r) => (
                            <th key={r} className="font-normal px-3 py-2 text-center whitespace-nowrap">{ROLE_LABEL[r] || r}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {group.items.map((item) => (
                          <tr key={item.capability} className="border-t border-page-divider align-top">
                            <td className="px-3 py-2.5">
                              <div className="text-page-text">{item.capability}</div>
                              {item.detail && <div className="text-xs text-page-muted mt-0.5">{item.detail}</div>}
                              {item.note && <div className="text-xs text-amber-700 mt-0.5">{item.note}</div>}
                            </td>
                            {roles.map((r) => (
                              <td key={r} className="px-3 py-2.5 text-center">
                                <div className="inline-flex justify-center"><Cell value={item.grants?.[r] || "no"} /></div>
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}

              {!scoped && (
                <p className="text-xs text-page-muted">
                  The project-access rule itself: <span className="font-mono">platform_admin ∨ owner ∨ org_admin(owning org) ∨
                  (org member ∧ org-visible) ∨ (org member ∧ explicit share)</span>. It fails closed — an unresolvable
                  project denies everyone. Enforced identically by the backend, the assistant MCP gate and the accounts
                  service, from one shared implementation.
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
